import React, { useState, useEffect } from "react";
import { Form, Input, Button, Space, Select, InputNumber } from "antd";
import { useTranslation } from "react-i18next";

import { API_URL } from "../../shared/utils/Util";
import { fetchT } from "../../shared/utils/TypedUtil";
import { useNotification } from "../../shared/hooks";
import {
  ItemSchemaGroup,
  CreateItemSchemaGroupRequest,
  UpdateItemSchemaGroupRequest,
} from "./types";

type ItemSchemaGroupFormProps = {
  restaurantId: string;
  onSuccess: () => void;
  editingGroup?: ItemSchemaGroup | null;
  onCancelEdit?: () => void;
};

export const ItemSchemaGroupForm: React.FC<ItemSchemaGroupFormProps> = ({
  restaurantId,
  onSuccess,
  editingGroup,
  onCancelEdit,
}) => {
  const { t } = useTranslation();
  const { createSuccess, updateSuccess, notifyError } = useNotification();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingGroup) {
      form.setFieldsValue({
        name: editingGroup.name,
        menu_type: editingGroup.menu_type,
        seq: editingGroup.seq,
      });
    } else {
      form.resetFields();
    }
  }, [editingGroup, form]);

  const onFinish = (values: {
    name: string;
    menu_type: "dine_in" | "large_volume";
    seq: number;
  }) => {
    setLoading(true);

    if (editingGroup) {
      const request: UpdateItemSchemaGroupRequest = {
        name: values.name,
        menu_type: values.menu_type,
        seq: values.seq,
      };

      fetchT<{ id: string }>(
        "PUT",
        `${API_URL}/v1/r/${restaurantId}/admin/item_schema_groups/${editingGroup.id}`,
        request,
        () => {
          updateSuccess(t("item_schema_group"));
          form.resetFields();
          onSuccess();
          setLoading(false);
        },
        (err) => {
          notifyError(t("failed_to_update_item_schema_group"), err);
          setLoading(false);
        }
      );
    } else {
      const request: CreateItemSchemaGroupRequest = {
        name: values.name,
        menu_type: values.menu_type,
        seq: values.seq,
      };

      fetchT<{ id: string }>(
        "POST",
        `${API_URL}/v1/r/${restaurantId}/admin/item_schema_groups`,
        request,
        () => {
          createSuccess(t("item_schema_group"));
          form.resetFields();
          onSuccess();
          setLoading(false);
        },
        (err) => {
          notifyError(t("failed_to_create_item_schema_group"), err);
          setLoading(false);
        }
      );
    }
  };

  const handleCancel = () => {
    form.resetFields();
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{ menu_type: "dine_in", seq: 0 }}
    >
      <Form.Item
        name="name"
        label={t("name")}
        rules={[{ required: true, message: t("name_is_required") }]}
      >
        <Input placeholder={t("enter_group_name")} />
      </Form.Item>

      <Form.Item
        name="menu_type"
        label={t("menu_type")}
        rules={[{ required: true, message: t("menu_type_is_required") }]}
      >
        <Select>
          <Select.Option value="dine_in">{t("dine_in_pickup")}</Select.Option>
          <Select.Option value="large_volume">{t("group_order")}</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        name="seq"
        label={t("sequence")}
        rules={[{ required: true, message: t("sequence_is_required") }]}
      >
        <InputNumber min={0} style={{ width: "100%" }} placeholder={t("enter_sequence")} />
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            {editingGroup ? t("save") : t("create")}
          </Button>
          <Button onClick={handleCancel}>{editingGroup ? t("cancel") : t("reset")}</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};
